/* eslint-disable react/display-name */
import React, { useState, forwardRef, useImperativeHandle, useEffect, useCallback } from 'react';
import Api from '../../api/global.service.js';
import { useAuth } from '../content/auth.jsx';
import * as yup from 'yup';
import { publish } from '../events.js';
import { getIdCatCreditoPrecosecha, useCoopConfig } from '../content/coop-config.jsx';
import useDeudaProductor from '../../hooks/useDeudaProductor.js';
import transformWord from '../../utils/transformWord.js';
import { formatNumber } from '../../utils/formatNumber.js';

const ModalRemanente = forwardRef((props, ref) => {
  const auth = useAuth();
  const coopConfig = useCoopConfig();
  const [show, setShow] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [errors, setErrors] = useState(null);
  const [preciosPorKg, setPreciosPorKg] = useState([]);
  const [cantidadPago, setCantidadPago] = useState(`Monto total a pagar (${coopConfig.currency()?.symbol})`);
  const { creditos } = useDeudaProductor({
    id_ciclo: props?.data?.id_ciclo,
    productor_uuid: props?.data?.productor_uuid,
  });
  const [etiquetaAnticipos, setEtiquetaAnticipos] = useState('-');
  const [observaciones, setObservaciones] = useState('');
  const yupInput = yup
    .number()
    .required('Debe escribir una cantidad')
    .min(0, 'Ingrese un número mayor que cero 0')
    .transform(value => (isNaN(value) ? undefined : value));

  const schema = yup.object().shape({
    cantidad_pago: yupInput,
    observaciones: yup.string().nullable(),
  });

  useEffect(() => {
    getEtiquetaAnticipo();
  }, [coopConfig.config]);

  const getEtiquetaAnticipo = async () => {
    let etiqueta = await coopConfig.etiquetaAnticipos();
    setEtiquetaAnticipos(etiqueta);
  };

  //To call outside
  useImperativeHandle(ref, () => ({
    show: showHandler,
  }));

  const showHandler = () => {
    setShow(true);
    setErrors(null);
    // Read fresh from props.data every time modal opens
    const tipos = props.data?.remanente_tipos || [];
    setPreciosPorKg(tipos.map(tipo => ({ ...tipo, precio_por_kg: parseFloat(formatNumber(tipo.precio_por_kg, 2)) })));
    setCantidad('cantidadPago', props.data?.cantidad_pago != null ? parseFloat(formatNumber(props.data.cantidad_pago, 2)) : props.data?.cantidad_pago);
    setObservaciones(props.data?.observaciones ?? '');
  };

  const closeHandler = () => {
    setShow(false);
  };

  const validateHandler = () => {
    const errorsObj = {};

    // Validate each precio individually
    preciosPorKg.forEach((tipo, index) => {
      try {
        const value = tipo.precio_por_kg ? parseInt(tipo.precio_por_kg * 100) : null;
        yupInput.validateSync(value);
      } catch (err) {
        errorsObj[`precio_por_kg_${index}`] = err.message;
      }
    });

    // Validate cantidad_pago
    try {
      const cantidadValue = cantidadPago ? parseInt(cantidadPago * 100) : null;
      yupInput.validateSync(cantidadValue);
    } catch (err) {
      errorsObj.cantidad_pago = err.message;
    }

    // If there are errors, setErrors and return
    if (Object.keys(errorsObj).length > 0) {
      setErrors(errorsObj);
      return;
    }

    // Build the remanente_tipos array with id and updated precio_por_kg
    const remanenteTipos = preciosPorKg.map(tipo => ({
      id: tipo.id,
      precio_por_kg: parseInt(tipo.precio_por_kg * 100),
    }));

    const dataVal = {
      remanente_tipos: remanenteTipos,
      cantidad_pago: cantidadPago ? parseInt(cantidadPago * 100) : null,
      observaciones: observaciones?.trim() || null,
    };

    schema
      .validate(dataVal, { abortEarly: false })
      .then(async function () {
        // data post
        const dataCoop = { id_cooperativa: auth.user.id_cooperativa };
        const data = { ...dataCoop, ...dataVal };
        // Post request
        setBlocking(true);
        const response = await Api.Acopio.update('remanente/generar_recibos', props.data.id, data);
        setBlocking(false);
        setShow(false);
        publish('Remanente::onSave', response.data.data);
      })
      .catch(function (err) {
        let errors = err.inner.reduce(function (map, obj) {
          map[obj.path] = obj.message;
          return map;
        }, {});
        setErrors(errors);
      });
  };

  const setCantidad = (inputName, inputVal) => {
    if (inputName.startsWith('precioPorKg_')) {
      const index = parseInt(inputName.split('_')[1], 10);
      setPreciosPorKg(prev => {
        const newPrecios = [...prev];
        newPrecios[index] = { ...newPrecios[index], precio_por_kg: inputVal };
        return newPrecios;
      });
    } else if (inputName === 'cantidadPago') {
      setCantidadPago(inputVal);
    }
  };

  const InputPrice = useCallback(({ label, inputName, inputVal, error, disabled, currency }) => {
    return (
      <>
        <label className="font-open-normal-400 body-0 subtitle-1">{label}</label>
        <div className="input-group mb-2">
          <div className="input-group-prepend">
            <i className="fa fa-search" style={{ padding: 14, color: '#ffffff' }}></i>
          </div>
          <input
            type="number"
            name={inputName}
            className={'form-control bg-white' + (disabled ? ' text-muted' : '')}
            value={inputVal}
            onChange={event => setCantidad(inputName, event.target.value)}
            disabled={disabled ? disabled : false}
          />
          <div className="input-group-append">
            <i className="fa fa-chevron-down" style={{ padding: 14, color: '#ffffff' }}></i>
            <span className={'p-5 pl-0 font-open-normal-400 body-0 subtitle-1' + (disabled ? ' text-muted' : '')}>{currency}</span>
          </div>
        </div>
        {error && <div className="invalid-feedback input-group border-0 mb-2">{error}</div>}
      </>
    );
  }, []);

  return (
    <div
      className="modal"
      style={{
        display: show ? 'block' : 'none',
        backgroundColor: 'rgba(0,0,0,0.2)',
        height: '100vh',
      }}>
      <div
        className="modal-dialog"
        style={{
          top: 40,
          right: 25,
          position: 'absolute',
          width: 400,
          maxHeight: 'calc(100vh - 80px)',
          height: 'calc(100vh - 80px)',
          margin: 0,
        }}>
        <div
          className="modal-content"
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
          <div className="modal-header" style={{ flex: 'inherit', flexShrink: 0 }}>
            <h4 className="modal-title">{props.title}</h4>
            <button onClick={() => closeHandler()} type="button" className="btn" style={{ borderRadius: '20px' }}>
              <i className="fa fa-times"></i>
            </button>
          </div>
          <div
            className="modal-body"
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}>
            <div className="row">
              <div className="col-12">
                <h5 className="font-lato-normal-500">
                  ¿Quieres actualizar este pago del productor <strong>{props.data?.productor_nombre}</strong>?
                </h5>
              </div>
            </div>
            <div className="row my-4">
              <div className="col-12">
                {props.data && (
                  <>
                    <InputPrice
                      label={`Peso neto (${coopConfig.hasAcopioUnitsExtra()
                        ? coopConfig.acopioUnitsExtra()?.abreviacion
                        : coopConfig.acopioUnits()?.abreviacion
                        })`}
                      inputName="pesoNeto"
                      inputVal={props.data.peso_neto}
                      currency={
                        coopConfig.hasAcopioUnitsExtra()
                          ? coopConfig.acopioUnitsExtra()?.abreviacion
                          : coopConfig.acopioUnits()?.abreviacion
                      }
                      error={null}
                      disabled={true}
                    />
                    {preciosPorKg.map((tipo, index) => (
                      <InputPrice
                        key={tipo.id}
                        label={`Precio ${tipo.cat_cert_nombre} ${coopConfig.currency()?.symbol}/${coopConfig.hasAcopioUnitsExtra()
                          ? coopConfig.acopioUnitsExtra()?.abreviacion
                          : coopConfig.acopioUnits()?.abreviacion
                          }`}
                        inputName={`precioPorKg_${index}`}
                        inputVal={tipo.precio_por_kg}
                        currency={`${coopConfig.currency()?.symbol}/${coopConfig.hasAcopioUnitsExtra()
                          ? coopConfig.acopioUnitsExtra()?.abreviacion
                          : coopConfig.acopioUnits()?.abreviacion
                          }`}
                        error={errors?.[`precio_por_kg_${index}`]}
                      />
                    ))}
                    <InputPrice
                      label={`Monto total a pagar ${coopConfig.currency()?.symbol}`}
                      inputName="cantidadPago"
                      inputVal={cantidadPago}
                      currency={coopConfig.currency()?.symbol}
                      error={errors?.cantidad_pago}
                    />
                  </>
                )}
              </div>
            </div>
            {props.data && (
              <div className="row my-4">
                <div className="col-12">
                  <label className="font-open-normal-400 body-0 subtitle-1">Observaciones</label>
                  <textarea
                    name="observaciones"
                    className={'form-control bg-white' + (errors?.observaciones ? ' is-invalid' : '')}
                    placeholder="Observaciones"
                    value={observaciones}
                    onChange={e => setObservaciones(e.target.value)}
                    rows={3}
                  />
                  {errors?.observaciones && <div className="invalid-feedback d-block">{errors.observaciones}</div>}
                </div>
              </div>
            )}
            {getIdCatCreditoPrecosecha() > 1 && creditos?.creditos_no > 0 && (
              <div className="pt-2 pb-2">
                <div className="row">
                  <div className="col-12">
                    <h5 className="font-lato-normal-500">
                      Dato Informativo de {transformWord(etiquetaAnticipos, ['plural']) + ' precosecha'}
                    </h5>
                  </div>
                  <div className="col-xl-4 col-lg-6 info-entregas-link">{''}</div>
                </div>

                <div className="row">
                  <div className="col-6 info-entregas-variable">{'Importe total'}</div>
                  <div className="col-6 info-entregas-variable">
                    {coopConfig.currency()?.symbol + ' ' + formatNumber(creditos?.creditos_total || 0, 2)}
                  </div>
                </div>

                <div className="row">
                  <div className="col-6 info-entregas-variable">{'Saldo del ' + transformWord(etiquetaAnticipos, ['singular'])}</div>
                  <div className="col-6 info-entregas-variable">
                    {coopConfig.currency()?.symbol + ' ' + formatNumber(creditos?.total_saldo || 0)}
                  </div>
                </div>
              </div>
            )}
            <div className="modal-footer" style={{ flexShrink: 0 }}>
              {blocking && (
                <div className="spinner-border text-warning" role="status">
                  <span className="sr-only">Guardando...</span>
                </div>
              )}
              {!blocking && (
                <>
                  <button
                    onClick={() => closeHandler()}
                    type="button"
                    className="btn btn-outline-secondary btn-sirio"
                    style={{ borderRadius: '20px 20px 20px 20px' }}>
                    <i className="fa fa-times"></i> Cancelar
                  </button>
                  <button
                    onClick={() => validateHandler()}
                    type="button"
                    className="btn btn-outline-secondary btn-sirio"
                    style={{ borderRadius: '20px 20px 20px 20px' }}>
                    <i className="fa fa-save"></i> Guardar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ModalRemanente;
